import { getStreamTextMode } from "./streamText";
const GENERIC_ERROR = "ไม่สามารถส่งข้อความได้ ลองใหม่อีกครั้ง";

const FRIENDLY_ERROR_MESSAGES = {
  chatbot_timeout: "ระบบตอบช้า กรุณาลองใหม่",
  chatbot_not_configured: "ระบบยังไม่พร้อมใช้งาน",
  chatbot_invalid_response: "รูปแบบข้อมูลตอบกลับไม่ถูกต้อง",
  chatbot_invalid_request: "ข้อมูลที่ส่งไม่ถูกต้อง",
  chatbot_upstream_error: "เกิดข้อผิดพลาดจากบริการปลายทาง",
  chatbot_upstream_unavailable: "บริการปลายทางไม่พร้อมใช้งาน",
};

function looksLikeMojibake(text) {
  return /(?:Ã.|Â.|à¸|à¹|àº|à)/.test(text);
}

function scoreReadableThai(text) {
  const thaiMatches = text.match(/[\u0E00-\u0E7F]/g) || [];
  const mojibakeMatches = text.match(/(?:Ã.|Â.|à¸|à¹|àº|à)/g) || [];
  return thaiMatches.length - mojibakeMatches.length * 2;
}

function normalizeStreamEventName(eventName = "message") {
  return String(eventName || "message").trim().toLowerCase();
}

function isThinkingEventName(eventName = "") {
  return /(think|thinking|plan|planning|tool|status|progress|log|trace|reason|analysis)/i.test(eventName);
}

function isFinalEventName(eventName = "") {
  return /(final|answer|response|completion|complete|assistant)/i.test(eventName);
}

export function repairMojibake(text) {
  if (typeof text !== "string" || !text) {
    return text;
  }

  if (!looksLikeMojibake(text) || typeof TextDecoder === "undefined") {
    return text;
  }

  try {
    const bytes = Uint8Array.from(Array.from(text), (char) => char.charCodeAt(0) & 0xff);
    const repaired = new TextDecoder("utf-8", { fatal: false }).decode(bytes);

    if (!repaired || repaired.includes("\ufffd")) {
      return text;
    }

    return scoreReadableThai(repaired) >= scoreReadableThai(text) ? repaired : text;
  } catch {
    return text;
  }
}

export function parseChatbotResponse(data) {
  const assistantMessage = repairMojibake(data?.assistant_message || "") || "ยังไม่มีข้อความตอบกลับจากระบบ";

  return {
    assistantMessage,
    metadata: data?.metadata || null,
  };
}

function extractTextFromObjectPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return "";
  }

  const choices = payload?.choices;
  if (Array.isArray(choices) && choices[0]) {
    const deltaContent = choices[0]?.delta?.content;
    const messageContent = choices[0]?.message?.content;

    if (typeof deltaContent === "string") {
      return deltaContent;
    }

    if (typeof messageContent === "string") {
      return messageContent;
    }
  }

  const candidates = [
    payload?.assistant_message,
    payload?.assistantMessage,
    payload?.content,
    payload?.text,
    payload?.delta,
    payload?.token,
    payload?.answer,
    payload?.message,
    payload?.chunk,
    payload?.response,
    payload?.output,
  ];

  const firstText = candidates.find((value) => typeof value === "string" && value.length > 0);
  return firstText || "";
}

function classifyStreamKind(eventName, payload, textChunk) {
  const normalizedEventName = normalizeStreamEventName(eventName);
  const payloadEventName = normalizeStreamEventName(payload?.event || payload?.type || payload?.kind || payload?.stage || payload?.status);

  if (normalizedEventName === "done" || payloadEventName === "done" || textChunk === "[DONE]") {
    return "done";
  }

  if (normalizedEventName === "error" || payloadEventName === "error" || payload?.code || payload?.detail) {
    return "error";
  }

  if (isThinkingEventName(normalizedEventName) || isThinkingEventName(payloadEventName)) {
    return "thinking";
  }

  if (isFinalEventName(normalizedEventName) || isFinalEventName(payloadEventName)) {
    return "final";
  }

  if (typeof payload?.role === "string" && payload.role.toLowerCase() === "assistant") {
    return "final";
  }

  return textChunk ? "final" : "unknown";
}

export function parseChatbotStreamEvent(eventFrame) {
  const eventName = normalizeStreamEventName(eventFrame?.event || "message");
  const rawData = typeof eventFrame?.data === "string" ? eventFrame.data : "";

  if (eventName === "done" || rawData === "[DONE]") {
    return {
      eventName,
      kind: "done",
      isDone: true,
      textChunk: "",
      metadata: null,
      code: "",
      detail: "",
      upstreamStatus: null,
    };
  }

  if (!rawData) {
    return {
      eventName,
      kind: "unknown",
      isDone: false,
      textChunk: "",
      metadata: null,
      code: "",
      detail: "",
      upstreamStatus: null,
    };
  }

  try {
    const parsed = JSON.parse(rawData);

    if (typeof parsed === "string") {
      const textChunk = repairMojibake(parsed);
      return {
        eventName,
        kind: classifyStreamKind(eventName, null, textChunk),
        textMode: getStreamTextMode(eventName, null),
        isDone: false,
        textChunk,
        metadata: null,
        code: "",
        detail: "",
        upstreamStatus: null,
      };
    }

    if (parsed && typeof parsed === "object") {
      const textChunk = repairMojibake(extractTextFromObjectPayload(parsed));
      return {
        eventName,
        kind: classifyStreamKind(eventName, parsed, textChunk),
        textMode: getStreamTextMode(eventName, parsed),
        isDone: false,
        textChunk,
        metadata: parsed?.metadata || null,
        code: parsed?.code || "",
        detail: parsed?.detail || "",
        upstreamStatus: parsed?.upstream_status || parsed?.metadata?.upstream_status || null,
      };
    }
  } catch {
    const textChunk = repairMojibake(rawData);
    return {
      eventName,
      kind: classifyStreamKind(eventName, null, textChunk),
      textMode: getStreamTextMode(eventName, null),
      isDone: false,
      textChunk,
      metadata: null,
      code: "",
      detail: "",
      upstreamStatus: null,
    };
  }

  return {
    eventName,
    kind: "unknown",
    isDone: false,
    textChunk: "",
    metadata: null,
    code: "",
    detail: "",
    upstreamStatus: null,
  };
}

export function mapChatbotError(error) {
  const status = error?.response?.status || error?.status || 0;
  const code = error?.response?.data?.code || error?.code || "chatbot_unknown_error";
  const detail = error?.response?.data?.detail || error?.message;
  const upstreamStatus = error?.response?.data?.metadata?.upstream_status || error?.response?.data?.upstream_status || null;
  const isAbort = code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError";

  if (isAbort) {
    return {
      code,
      status,
      upstreamStatus,
      isAbort: true,
      message: "",
    };
  }

  if (status === 401) {
    return {
      code,
      status,
      upstreamStatus,
      isAbort: false,
      message: "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่",
    };
  }

  if (status === 403) {
    return {
      code,
      status,
      upstreamStatus,
      isAbort: false,
      message: "คุณไม่มีสิทธิ์เข้าถึงข้อมูลของ line/plant นี้",
    };
  }

  if (code === "ECONNABORTED" || status === 504 || code === "chatbot_timeout") {
    return {
      code: "chatbot_timeout",
      status,
      upstreamStatus,
      isAbort: false,
      message: FRIENDLY_ERROR_MESSAGES.chatbot_timeout,
    };
  }

  if (status === 502 || status === 503) {
    return {
      code: "chatbot_upstream_unavailable",
      status,
      upstreamStatus,
      isAbort: false,
      message: FRIENDLY_ERROR_MESSAGES.chatbot_upstream_unavailable,
    };
  }

  if (FRIENDLY_ERROR_MESSAGES[code]) {
    return {
      code,
      status,
      upstreamStatus,
      isAbort: false,
      message: FRIENDLY_ERROR_MESSAGES[code],
    };
  }

  return {
    code,
    status,
    upstreamStatus,
    isAbort: false,
    message: detail || GENERIC_ERROR,
  };
}
